import type { ErrorEvent } from "@sentry/react";

/**
 * Sentry "noise" filters: errors that are NOT bugs in our app. They originate
 * from third-party tools outside our control and do not affect the user flow
 * (Users Impacted: 0). We drop them in the SDK so they are never sent, avoiding
 * notifications that are pure noise.
 *
 * If more third-party noise shows up in the future, add the pattern/source here.
 */

/** Error-message patterns to ignore (Sentry `ignoreErrors` option). */
export const IGNORE_ERROR_PATTERNS: RegExp[] = [
  // SSO-REACT-PROD-84 / SSO-REACT-PROD-8J / SSO-REACT-PROD-9J — AWS
  // CloudWatch RUM SDK (`aws-rum-web`, bundled). Its anonymous Cognito auth
  // flow flakes on network errors while sending telemetry, throwing at three
  // different steps:
  //   - "CWR: Failed to retrieve Cognito identity"        (getId)
  //   - "CWR: Failed to retrieve Cognito OpenId token..."  (getOpenIdToken)
  //   - "CWR: Failed to retrieve credentials from STS..."  (assumeRoleWithWebIdentity)
  // Match the shared prefix to cover all of them. Note: this is the only
  // filter that works client-side for RUM noise — denyUrls /
  // THIRD_PARTY_NOISE_SOURCES inspect frame filenames, which point at the
  // minified bundle in `beforeSend` (source maps are applied server-side, after
  // the event is sent). The "CWR:" prefix is unique to the SDK, so it won't
  // collide with our code.
  /CWR: Failed to retrieve /,
  // SSO-REACT-PROD-8K — Meta in-app browser (Instagram/Facebook) injected JS.
  // Their WebView injects a script (`setupIosCallbackHandler`) that probes the
  // iOS native bridge `window.webkit.messageHandlers`, which doesn't exist in
  // that context, throwing "undefined is not an object (evaluating
  // 'window.webkit.messageHandlers')". We can't denyUrls here because the frame
  // is attributed to our own page (e.g. /full-login). Match the message: our
  // code never references `window.webkit.messageHandlers`, so this can only
  // come from injected third-party scripts. (In the real native iOS app the
  // bridge exists and this never throws.)
  /window\.webkit\.messageHandlers/,
];

/** Origin URLs to deny (Sentry `denyUrls` option). */
export const DENY_URLS: RegExp[] = [
  // SSO-REACT-PROD-8C — Ours Privacy third-party script
  // (https://cdn.oursprivacy.com/main.js). Its handler throws
  // "n.target.closest is not a function" on a target without `.closest()`.
  /cdn\.oursprivacy\.com/i,
  // SSO-REACT-PROD-8H — Facebook in-app browser injected JS. When the user
  // opens the hub inside the Facebook app's WebView, FB injects its own
  // telemetry scripts under the `iabjs://` (In-App Browser JS) scheme. On
  // `beforeunload` the WebView is torn down and FB's logger throws
  // "Error invoking enableDidUserTypeOnKeyboardLogging: Java object is gone".
  // The `iabjs://` scheme is exclusive to that injected code — our app only
  // ever loads over https, so this cannot match first-party frames.
  /^iabjs:\/\//i,
];

/**
 * Stacktrace origins (frame filename / module) known to be third-party noise.
 * Safety net for cases where `denyUrls` / `ignoreErrors` miss (minification, or
 * the relevant frame being wrapped by @sentry's instrumentation).
 */
const THIRD_PARTY_NOISE_SOURCES: RegExp[] = [
  /oursprivacy/i, // SSO-REACT-PROD-8C
  /aws-rum/i, // SSO-REACT-PROD-84
  /^iabjs:\/\//i, // SSO-REACT-PROD-8H — Facebook in-app browser injected JS
];

/**
 * Returns `true` if any frame in the event's stacktrace originates from a known
 * third-party noise source. Pure function.
 */
export const isThirdPartyNoise = (event: ErrorEvent): boolean => {
  const exceptionValues = event.exception?.values;

  if (!exceptionValues) {
    return false;
  }

  return exceptionValues.some((value) =>
    value.stacktrace?.frames?.some((frame) => {
      const origin = `${frame.filename ?? ""} ${frame.module ?? ""}`;

      return THIRD_PARTY_NOISE_SOURCES.some((pattern) => pattern.test(origin));
    }),
  );
};
